import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Plus, LogOut, ArrowRight, Feather } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Orb } from "@/components/Orb";

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [interviews, setInterviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);

  const load = async () => {
    try {
      const res = await api.get("/interviews");
      setInterviews(res.data);
    } catch (e) {
      toast.error("Couldn't load your reflections.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const beginNew = async () => {
    setStarting(true);
    try {
      const res = await api.post("/interviews");
      navigate(`/interview/${res.data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start the conversation.");
      setStarting(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  return (
    <div className="bf-noise relative min-h-screen overflow-hidden">
      <Orb stage={2} total={8} size={560} />

      <header className="relative z-10 mx-auto flex w-full max-w-3xl items-center justify-between px-6 py-6 sm:px-8">
        <div className="flex items-center gap-2 font-sans text-xs uppercase tracking-[0.18em] text-bf-muted">
          <Feather className="h-4 w-4 text-bf-gold" /> Bliss Factory
        </div>
        <div className="flex items-center gap-3">
          {user?.picture && (
            <img src={user.picture} alt="" className="h-8 w-8 rounded-full border border-white/15" />
          )}
          <button
            data-testid="logout-button"
            onClick={handleLogout}
            className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1.5 font-sans text-xs text-bf-muted transition-colors duration-300 hover:border-white/30 hover:text-bf-text"
          >
            <LogOut className="h-3.5 w-3.5" /> Sign out
          </button>
        </div>
      </header>

      <main className="relative z-10 mx-auto w-full max-w-3xl px-6 pb-24 pt-6 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
        >
          <h1 className="font-serif text-4xl leading-tight tracking-tight text-bf-text sm:text-5xl">
            Hello{user?.name ? `, ${user.name.split(" ")[0]}` : ""}.
          </h1>
          <p className="mt-4 max-w-lg font-sans text-base leading-relaxed text-bf-muted">
            Whenever you're ready, settle in for a conversation — or revisit a
            reflection you've saved before.
          </p>

          <button
            data-testid="begin-conversation-button"
            onClick={beginNew}
            disabled={starting}
            className="mt-8 inline-flex items-center gap-2.5 rounded-full bg-gradient-to-r from-bf-gold to-bf-rose px-6 py-3.5 font-sans text-sm font-medium text-[#2A1521] shadow-[0_0_40px_-10px_rgba(255,180,90,0.6)] transition-transform duration-300 hover:scale-[1.03] disabled:opacity-60"
          >
            <Plus className="h-4 w-4" />
            {starting ? "Opening a space for you…" : "Begin a new conversation"}
          </button>
        </motion.div>

        <div className="mt-14">
          <h2 className="mb-5 font-sans text-xs uppercase tracking-[0.18em] text-bf-muted/70">
            Your reflections
          </h2>

          {loading ? (
            <div className="py-10 text-center font-sans text-sm text-bf-muted/60">Loading…</div>
          ) : interviews.length === 0 ? (
            <div
              data-testid="empty-state"
              className="rounded-2xl border border-white/10 bg-white/[0.03] px-6 py-12 text-center backdrop-blur-sm"
            >
              <p className="font-serif text-xl text-bf-text">Nothing saved yet.</p>
              <p className="mt-2 font-sans text-sm text-bf-muted">
                Your first reflection will live here once you finish a conversation.
              </p>
            </div>
          ) : (
            <div className="space-y-4" data-testid="reflections-list">
              {interviews.map((it, i) => (
                <motion.button
                  key={it.id}
                  data-testid={`reflection-card-${it.id}`}
                  onClick={() => navigate(`/interview/${it.id}`)}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: i * 0.06 }}
                  className="group block w-full rounded-2xl border border-white/10 bg-white/[0.03] p-6 text-left backdrop-blur-sm transition-colors duration-300 hover:border-bf-gold/40"
                >
                  <div className="flex items-center justify-between">
                    <span
                      className={`rounded-full px-3 py-1 font-sans text-[11px] uppercase tracking-[0.12em] ${
                        it.status === "completed"
                          ? "bg-bf-gold/15 text-bf-gold"
                          : "bg-white/10 text-bf-muted"
                      }`}
                    >
                      {it.status === "completed" ? "Reflection saved" : `In progress · ${it.current_stage}/${it.total_questions}`}
                    </span>
                    <ArrowRight className="h-4 w-4 text-bf-muted transition-transform duration-300 group-hover:translate-x-1 group-hover:text-bf-gold" />
                  </div>
                  <p className="mt-4 font-serif text-lg leading-relaxed text-bf-text">
                    {it.preview
                      ? `${it.preview}${it.preview.length >= 160 ? "…" : ""}`
                      : "A conversation you started — pick up where you left off."}
                  </p>
                  <p className="mt-3 font-sans text-xs text-bf-muted/60">
                    {formatDate(it.completed_at || it.created_at)}
                  </p>
                </motion.button>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: "long", day: "numeric", year: "numeric",
    });
  } catch (e) {
    return "";
  }
}
